import { query as stardogQuery } from 'stardog';

import { request } from 'src/common/actions/request';
import {
  ActionCreatorAction,
  createTypedAction,
} from 'src/common/actions/types';
import { StoredQuery } from 'src/common/store/storedQueries/StoredQueriesState';
import { rdfAcceptTypes } from 'src/common/utils/acceptTypes';
import { serializeStoredQuery } from 'src/common/utils/jsonld/serializeStoredQuery';

export enum StoredQueriesActionType {
  GET_STORED_QUERY_ATTEMPT = 'GET_STORED_QUERY_ATTEMPT',
  GET_STORED_QUERY_SUCCESS = 'GET_STORED_QUERY_SUCCESS',
  GET_STORED_QUERY_FAILURE = 'GET_STORED_QUERY_FAILURE',
  GET_STORED_QUERIES_ATTEMPT = 'GET_STORED_QUERIES_ATTEMPT',
  GET_STORED_QUERIES_SUCCESS = 'GET_STORED_QUERIES_SUCCESS',
  GET_STORED_QUERIES_FAILURE = 'GET_STORED_QUERIES_FAILURE',
  DELETE_STORED_QUERY_ATTEMPT = 'DELETE_STORED_QUERY_ATTEMPT',
  DELETE_STORED_QUERY_SUCCESS = 'DELETE_STORED_QUERY_SUCCESS',
  DELETE_STORED_QUERY_FAILURE = 'DELETE_STORED_QUERY_FAILURE',
  REMOVE_UNAVAILABLE_STORED_QUERY = 'REMOVE_UNAVAILABLE_STORED_QUERY',
  RENAME_QUERY_ATTEMPT = 'RENAME_QUERY_ATTEMPT',
  RENAME_QUERY_SUCCESS = 'RENAME_QUERY_SUCCESS',
  RENAME_QUERY_FAILURE = 'RENAME_QUERY_FAILURE',
  STORE_QUERY_ATTEMPT = 'STORE_QUERY_ATTEMPT',
  STORE_QUERY_SUCCESS = 'STORE_QUERY_SUCCESS',
  STORE_QUERY_FAILURE = 'STORE_QUERY_FAILURE',
  UPDATE_QUERY_ATTEMPT = 'UPDATE_QUERY_ATTEMPT',
  UPDATE_QUERY_SUCCESS = 'UPDATE_QUERY_SUCCESS',
  UPDATE_QUERY_FAILURE = 'UPDATE_QUERY_FAILURE',
}

export const storedQueriesActionCreators = {
  removeUnavailableStoredQuery: (queryName: string) =>
    createTypedAction(StoredQueriesActionType.REMOVE_UNAVAILABLE_STORED_QUERY, {
      queryName,
    }),
};

export const storedQueriesStardogRequestDispatchers = {
  deleteStoredQuery: (queryName: string, database: string) =>
    request({
      api: stardogQuery.stored.remove,
      args: [queryName],
      action: {
        queryName,
        database,
      },
      loading: StoredQueriesActionType.DELETE_STORED_QUERY_ATTEMPT,
      success: StoredQueriesActionType.DELETE_STORED_QUERY_SUCCESS,
      failure: StoredQueriesActionType.DELETE_STORED_QUERY_FAILURE,
    }),
  getStoredQueries: (isGraphQLStoredQueriesSupported: boolean) =>
    request({
      api: stardogQuery.stored.list,
      args: [{ accept: rdfAcceptTypes['json-ld'].acceptType }],
      action: { isGraphQLStoredQueriesSupported },
      loading: StoredQueriesActionType.GET_STORED_QUERIES_ATTEMPT,
      success: StoredQueriesActionType.GET_STORED_QUERIES_SUCCESS,
      failure: StoredQueriesActionType.GET_STORED_QUERIES_FAILURE,
      shouldProcessJsonLd: true,
    }),
  getStoredQuery: (name: string, isGraphQLStoredQueriesSupported: boolean) =>
    request({
      api: stardogQuery.stored.get,
      args: [name, { accept: rdfAcceptTypes['json-ld'].acceptType }],
      action: { isGraphQLStoredQueriesSupported, name },
      loading: StoredQueriesActionType.GET_STORED_QUERY_ATTEMPT,
      success: StoredQueriesActionType.GET_STORED_QUERY_SUCCESS,
      failure: StoredQueriesActionType.GET_STORED_QUERY_FAILURE,
      shouldProcessJsonLd: true,
    }),
  renameQueryForNote: (oldName: string, newName: string) =>
    request({
      api: stardogQuery.stored.rename,
      args: [oldName, newName],
      action: { oldName, newName },
      loading: StoredQueriesActionType.RENAME_QUERY_ATTEMPT,
      success: StoredQueriesActionType.RENAME_QUERY_SUCCESS,
      failure: StoredQueriesActionType.RENAME_QUERY_FAILURE,
    }),
  storeQueryForNote: (
    storeQueryOptions: Pick<
      StoredQuery,
      // `reasoning` is added in 6.2.2, but it does no harm to provide it
      // earlier, at least tested with 6.2.0
      | 'creator'
      | 'name'
      | 'database'
      | 'query'
      | 'schema'
      | 'shared'
      | 'reasoning'
      | 'tags'
    >,
    updateExamplesFailed?: boolean
  ) =>
    request({
      api: stardogQuery.stored.create,
      args: [
        serializeStoredQuery(storeQueryOptions),
        {
          accept: 'application/ld+json',
          contentType: 'application/ld+json',
        },
      ],
      action: { storeQueryOptions, updateExamplesFailed },
      loading: StoredQueriesActionType.STORE_QUERY_ATTEMPT,
      success: StoredQueriesActionType.STORE_QUERY_SUCCESS,
      failure: StoredQueriesActionType.STORE_QUERY_FAILURE,
    }),
  updateQueryForNote: (
    storedQueryOptions: Pick<
      StoredQuery,
      // `reasoning` is added in 6.2.2, but it does no harm to provide it
      // earlier, at least tested with 6.2.0
      | 'creator'
      | 'name'
      | 'database'
      | 'query'
      | 'schema'
      | 'shared'
      | 'reasoning'
      | 'tags'
    >,
    latestSavedVersionId: number,
    updateExamplesFailed?: boolean
  ) =>
    request({
      api: stardogQuery.stored.update,
      args: [
        serializeStoredQuery(storedQueryOptions),
        {
          accept: 'application/ld+json',
          contentType: 'application/ld+json',
        },
      ],
      action: {
        latestSavedVersionId,
        storedQueryOptions,
        updateExamplesFailed,
      },
      loading: StoredQueriesActionType.UPDATE_QUERY_ATTEMPT,
      success: StoredQueriesActionType.UPDATE_QUERY_SUCCESS,
      failure: StoredQueriesActionType.UPDATE_QUERY_FAILURE,
    }),
};

const storedQueriesActionTypeMap = {
  ...storedQueriesActionCreators,
  ...storedQueriesStardogRequestDispatchers,
};

export type StoredQueriesAction = ActionCreatorAction<
  typeof storedQueriesActionTypeMap
>;
